import React, { useCallback, useState } from 'react';
import { Typography, Button, TextField } from '@mui/material';
import Box from '@mui/material/Box';
import { useNavigate, useLocation } from 'react-router';
import PropTypes from 'prop-types';
import Http from 'services/Http';
import { handleLogin } from 'utils/auth';
import { I18n } from 'utils/i18n';
import usePasskeyAuth from 'utils/usePasskeyAuth';
import connector from '../connector';

function PasskeyCancelledScreen({ actions }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { authenticateWithPasskey } = usePasskeyAuth();

  // Get state from location (phone number and error info)
  const {
    phoneNumber,
    error: initialError,
    countryCode,
    isInitialPrompt,
    passwordLogin,
    ...rest
  } = location.state || {};

  // Track current error (can be updated if user cancels again)
  const [currentError, setCurrentError] = useState(initialError);
  const [isRetrying, setIsRetrying] = useState(false);
  const [pendingId, setPendingId] = useState(null);
  const [otpCode, setOtpCode] = useState('');
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const [otpError, setOtpError] = useState(null);

  const clearUpgradeState = useCallback(() => {
    setPendingId(null);
    setOtpCode('');
    setOtpError(null);
  }, []);

  const sendUpgradeOtp = useCallback(async id => {
    await Http.post('/v1/webauthn/authenticate/send-otp', { pendingId: id });
  }, []);

  const beginUpgradeFlow = useCallback(
    async (id, phone, prefix) => {
      setPendingId(id);
      setOtpCode('');
      setOtpError(null);
      try {
        await sendUpgradeOtp(id);
      } catch (err) {
        setOtpError(
          err?.data?.error?.message ||
            I18n.t('LoginConfirmation.otp_send_error', 'Failed to send verification code'),
        );
      }
      return { phone, prefix };
    },
    [sendUpgradeOtp],
  );

  // Handle SMS fallback
  const handleSendSMS = useCallback(async () => {
    if (!phoneNumber) {
      navigate('/auth/login');
      return;
    }

    try {
      // First update the form state
      if (actions?.login?.form?.update) {
        await actions.login.form.update({
          prefix: countryCode,
          phone: phoneNumber,
        });
      }

      // Use the Redux action instead of direct API call
      // This will handle the state updates and navigation properly
      if (actions?.login?.sendSMS) {
        await actions.login.sendSMS(`${countryCode}${phoneNumber}`, {
          ...rest,
        });

        // Navigate to confirmation page directly
        navigate('/auth/login/confirmation', { replace: true });
      } else {
        console.error('SMS send action not available');
        navigate('/auth/login');
      }
    } catch (smsError) {
      console.error('Error sending SMS:', smsError);
      navigate('/auth/login');
    }
  }, [phoneNumber, countryCode, navigate, actions, rest]);

  // Handle retry passkey authentication
  const handleRetryPasskey = useCallback(async () => {
    if (!phoneNumber) {
      navigate('/auth/login');
      return;
    }

    setIsRetrying(true);
    clearUpgradeState();

    const options = {
      countryCode,
      skipNavigateOnCancel: true, // Don't navigate if user cancels again
      ...rest,
    };

    const result = await authenticateWithPasskey(phoneNumber, countryCode, options);

    setIsRetrying(false);

    if (result.upgradeRequired && result.pendingId) {
      await beginUpgradeFlow(result.pendingId, result.phoneNumber, result.prefix);
      return;
    }

    // If user cancelled again, update the error but stay on this screen
    if (result.canceled) {
      setCurrentError(result.error);
      return;
    }

    // If passkey no longer available, go to SMS flow
    if (!result.passkey) {
      handleSendSMS();
    }
  }, [
    phoneNumber,
    countryCode,
    navigate,
    authenticateWithPasskey,
    handleSendSMS,
    rest,
    clearUpgradeState,
    beginUpgradeFlow,
  ]);

  const handleVerifyUpgradeOtp = useCallback(async () => {
    if (!pendingId || !otpCode.trim() || verifyingOtp) {
      return;
    }
    setVerifyingOtp(true);
    setOtpError(null);
    try {
      const result = await Http.post('/v1/webauthn/authenticate/verify-otp', {
        pendingId,
        code: otpCode.trim(),
        keepSessionOpen: rest.keepSessionOpen,
      });
      const token = result?.data?.token;
      const user = result?.data?.user;
      if (token?.hash) {
        handleLogin(token, user, rest.keepSessionOpen, rest.returnUrl);
        return;
      }
      setOtpError(I18n.t('LoginConfirmation.otp_verify_error', 'Invalid verification code'));
    } catch (err) {
      setOtpError(
        err?.data?.error?.message ||
          I18n.t('LoginConfirmation.otp_verify_error', 'Invalid verification code'),
      );
    } finally {
      setVerifyingOtp(false);
    }
  }, [pendingId, otpCode, verifyingOtp, rest.keepSessionOpen, rest.returnUrl]);

  const handleCancelUpgrade = useCallback(() => {
    clearUpgradeState();
  }, [clearUpgradeState]);

  // Different messaging for initial prompt vs after cancellation
  const headingText = pendingId
    ? I18n.t('LoginConfirmation.passkey_upgrade_otp', 'Enter the SMS code to verify this passkey')
    : isInitialPrompt
      ? I18n.t('LoginConfirmation.passkey_available', 'Passkey available')
      : I18n.t('LoginConfirmation.passkey_cancelled');

  const passkeyButtonText = isInitialPrompt
    ? I18n.t('LoginConfirmation.login_with_passkey', 'Login with passkey')
    : I18n.t('LoginConfirmation.try_passkey_again');

  const containerSx = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    mt: 4,
    p: 2,
  };

  const messageSx = {
    mb: 4,
    textAlign: 'center',
    fontWeight: 500,
  };

  const buttonSx = {
    m: 1,
    width: '100%',
    maxWidth: 300,
  };

  const errorDetailSx = {
    color: 'text.secondary',
    mb: 2,
    textAlign: 'center',
  };

  const otpFieldSx = {
    width: '100%',
    maxWidth: 300,
    mb: 2,
  };

  if (pendingId) {
    return (
      <Box sx={containerSx}>
        <Typography variant="h5" sx={messageSx}>
          {headingText}
        </Typography>
        {otpError && (
          <Typography variant="body2" sx={errorDetailSx}>
            {otpError}
          </Typography>
        )}
        <TextField
          sx={otpFieldSx}
          label={I18n.t('LoginConfirmation.otp_code', 'SMS code')}
          value={otpCode}
          onChange={e => setOtpCode(e.target.value)}
          slotProps={{ htmlInput: { inputMode: 'numeric', autoComplete: 'one-time-code' } }}
          disabled={verifyingOtp}
        />
        <Button
          variant="contained"
          color="primary"
          sx={buttonSx}
          onClick={handleVerifyUpgradeOtp}
          disabled={verifyingOtp || !otpCode.trim()}
        >
          {verifyingOtp
            ? I18n.t('Verifying')
            : I18n.t('LoginConfirmation.verify_otp', 'Verify code')}
        </Button>
        <Button
          variant="outlined"
          color="primary"
          sx={buttonSx}
          onClick={handleCancelUpgrade}
          disabled={verifyingOtp}
        >
          {I18n.t('Cancel')}
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={containerSx}>
      <Typography variant="h5" sx={messageSx}>
        {headingText}
      </Typography>

      {currentError && currentError.message && (
        <Typography variant="body2" sx={errorDetailSx}>
          {I18n.t(`PasskeyErrors.${currentError.code}`) || currentError.message}
        </Typography>
      )}

      <Button
        variant="contained"
        color="primary"
        sx={buttonSx}
        onClick={handleRetryPasskey}
        disabled={isRetrying}
      >
        {isRetrying ? I18n.t('Verifying') : passkeyButtonText}
      </Button>

      <Button
        variant="outlined"
        color="primary"
        sx={buttonSx}
        onClick={handleSendSMS}
        disabled={isRetrying}
      >
        {passwordLogin
          ? I18n.t('LoginConfirmation.use_password', 'Use password')
          : I18n.t('LoginConfirmation.send_sms_code')}
      </Button>
    </Box>
  );
}

PasskeyCancelledScreen.propTypes = {
  actions: PropTypes.shape({
    login: PropTypes.shape({
      form: PropTypes.shape({
        update: PropTypes.func,
      }),
      sendSMS: PropTypes.func,
    }),
  }).isRequired,
};

export default connector(PasskeyCancelledScreen);
